import RestoreIcon from "@mui/icons-material/Restore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { type ReactNode, useState } from "react";
import type Individual from "marketplace/individuals/individual";
import useIndividualCardTrigger from "marketplace/individuals/profiles/_views/use-individual-card-trigger";
import ProposalPendingReviewAlert from "work/proposals/_views/_shared/proposal-pending-review-alert";
import {
  proposalChangeRedlineValues,
  proposalChangeTransitionCarriesItemLabel,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import {
  proposalChangeRestoredValueText,
  summarizeProposalFeeScheduleRestoredDescriptors,
} from "work/proposals/negotiation/proposal-change-restored-value";
import { groupProposalChangesBySection } from "work/proposals/negotiation/proposal-change-sections";
import ProposalChangeValueRedline from "work/proposals/negotiation/proposal-change-value-redline";
import ProposalFeeScheduleChangeRedline from "work/proposals/negotiation/proposal-fee-schedule-change-redline";
import {
  partitionProposalFeeScheduleChanges,
  summarizeProposalFeeScheduleDescriptors,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";

/**
 * The change list in two columns on a wide dialog.
 *
 * A submission's list is as long as the proposal is big, and read as one column it was a wall the
 * reader scrolled past rather than checked (#4538). Two columns halve the height without dropping a
 * row, which is the only outcome that suits BOTH surfaces that still draw a list: a revision of a
 * hired work agreement, where the list is informational, and the resubmission, where each row carries
 * the Revert button that is the whole point of showing it. A negotiation's genuine first submission
 * has nothing published behind it to differ from and passes no changes at all (#4952).
 *
 * `columnCount` rather than a grid: sections are different heights, and a two-column grid would align
 * them in rows and leave a gap beside every short section. Flowing them balances the columns instead.
 * A section never splits across the fold — a heading stranded at the bottom of the left column would
 * file its rows under the wrong subject — so the unit that avoids the break is the whole section.
 *
 * One column below `md`. Two columns of a narrow dialog is worse than one: the fee-schedule redline is
 * a horizontal composition ("Notice of Opposition — Fixed Fee: $4,800 | Currency: USD") that wraps into
 * a paragraph and stops reading as a redline once a column is much under 400px.
 */
const ChangeColumns = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
    columnGap: theme.spacing(4),
  },
}));

const ChangeSection = styled("div")(() => ({
  breakInside: "avoid",
}));

/**
 * A change row is a flex line: the change on the left, its Revert button on the right. Both halves have
 * to be told what to do when the row runs out of room, or a wrapping value slides under the button —
 * which a narrow column makes routine rather than rare.
 */
const changeRowSx = { pl: 2, gap: 2, alignItems: "flex-start" } as const;

/**
 * The recipient's name: bold, in the sentence's own colour, and a button, because pointing at it opens
 * their business card and a click pins the card (see `useIndividualCardTrigger`). Underlined on hover
 * only, so it reads as a name first and a control second.
 */
const RecipientName = styled(Link)(({ theme }) => ({
  font: "inherit",
  fontWeight: theme.typography.fontWeightBold,
  verticalAlign: "baseline",
  // Cast back to `Link` so `component="button"` keeps its typing; `styled` drops MUI's overloads.
})) as typeof Link;
const revertButtonSx = { flexShrink: 0 } as const;

/**
 * The one dialog a submission goes through.
 *
 * It used to be two: a plain "Submit the proposal now?" confirm, and then — only sometimes, and only
 * after the save had already run — a second dialog asking which changes to send. Two dialogs for one
 * decision meant the user confirmed before seeing what they were confirming. This one names every
 * change in the same words the field change history uses, says who receives it, and lets the leader
 * explicitly restore an unwanted change before the whole proposal is submitted. Who receives it is named
 * by party and person — "the vendor: John Doe" — and said before anything else; the person's name opens
 * their business card, which carries their firm (#5813).
 *
 * The changes are grouped by subject (see `proposal-change-sections.ts`) rather than listed in the order
 * they were made, following the proposal tab order, and no row is ever left with a blank value: a change
 * with nothing on its new side reads "[Empty]".
 *
 * Every row says what it will submit, reverted or not. A reverted row used to say only that it would be
 * "restored before submission", which named the mechanism and left the reader unable to see what the
 * system would send for that one field; it draws the restored value now, plainly (#5551). See
 * `proposal-change-restored-value.ts`.
 *
 * It is about changes and nothing else. It used to also list the author's unpublished comments and ask
 * whether to send them, because a comment was staged until a save or submit delivered it; a comment is
 * published as it is posted now (see `proposal-comment-publishing.ts`), so a submission has no comment
 * decision left to put to the user.
 */

export type SubmitProposalDialogProps = {
  title: string;
  /** Everything this submission would send, already described in the shared change vocabulary. */
  changes: readonly ProposalChangeDescriptor[];
  /** Which party receives it — "the vendor", "the client". */
  audienceLabel: string;
  /**
   * Who at that party receives it: the leader of their team, as the directory knows them. Absent while
   * the name is unknown, and the sentence then names the party alone.
   */
  recipient?: Individual;
  /**
   * Heading over the list: "Proposal summary" for a first-time offer, "Changes to the Work Agreement"
   * for a revision of a hired one, "Changes since your last submission" for a re-submission. See
   * `proposalSubmissionSummaryHeading`.
   */
  changesHeading: string;
  /** Whether the leader can restore values from an earlier submission. */
  allowRevert: boolean;
  /**
   * Warning above the list — that the leader's own reviewers have not finished with what is about to
   * be sent. See `proposal-pending-review-warning.ts`; absent when there is nothing to warn about.
   */
  message?: string;
  /**
   * Drawn under the warning: who those reviewers are, each with a way to remind them (#6618). See
   * `PendingReviewerReminders`; absent when the warning is, or when nobody can be named.
   */
  pendingReviewers?: ReactNode;
  confirmButtonText: string;
  onSubmit: (result: SubmitProposalDialogResult) => void;
  onCancel: () => void;
};

export type SubmitProposalDialogResult = {
  revertedChanges: readonly ProposalChangeDescriptor[];
};

export default function SubmitProposalDialog({
  title,
  changes,
  audienceLabel,
  recipient,
  changesHeading,
  allowRevert,
  message,
  pendingReviewers,
  confirmButtonText,
  onSubmit,
  onCancel,
}: Readonly<SubmitProposalDialogProps>) {
  const [revertedKeys, setRevertedKeys] = useState<readonly string[]>([]);
  // A pinned card stays on this dialog: following it to the profile would drop the submission in flight.
  // With nothing on it to act on, it also lets every click through to what is under it, so Cancel and
  // Submit stay reachable wherever it lands, and that click puts the card down too.
  //
  // It opens above the name, where only the dialog's title is, so it does not hide the change list or
  // the buttons either. Below the name is only where it goes when a short window has no room above.
  const recipientCard = useIndividualCardTrigger(recipient, {
    linksToProfile: false,
    placement: "top-start",
    fallbackPlacements: ["bottom-start"],
  });

  const isReverted = (change: ProposalChangeDescriptor) => revertedKeys.includes(change.key);
  const revertedChanges = changes.filter(isReverted);
  const sections = groupProposalChangesBySection(changes);

  function toggleRevert(change: ProposalChangeDescriptor) {
    setRevertedKeys((current) =>
      current.includes(change.key) ? current.filter((key) => key !== change.key) : [...current, change.key],
    );
  }

  /**
   * Reverts (or keeps) a group of changes as one act.
   *
   * The fee schedule is shown as one thing, so it has to revert as one thing: reverting only the leaf
   * a reader happened to click would leave the schedule half-restored, in a state nobody chose.
   */
  function toggleRevertAll(changes: readonly ProposalChangeDescriptor[], isCurrentlyReverted: boolean) {
    const keys = changes.map((change) => change.key);
    setRevertedKeys((current) =>
      isCurrentlyReverted
        ? current.filter((key) => !keys.includes(key))
        : [...current, ...keys.filter((key) => !current.includes(key))],
    );
  }

  function handleSubmit() {
    onSubmit({ revertedChanges });
  }

  /**
   * Width follows the list, because the list is the only thing that needs the room: two columns of
   * change rows want `md`, and a submission that shows no list at all — a negotiation's genuine first
   * one — is a plain confirmation, which at `md` was a near-empty box the width of the page around one
   * sentence (#5128). `sm` is what every other confirmation in the app is (see `confirm-dialog.tsx`),
   * so the two read as the same kind of question.
   */
  const listIsShown = changes.length > 0;

  return (
    <Dialog
      open
      onClose={onCancel}
      fullWidth
      maxWidth={listIsShown ? "md" : "sm"}
      aria-labelledby="submit-proposal-title"
      aria-describedby={allowRevert ? "submit-proposal-recipient submit-proposal-revert" : "submit-proposal-recipient"}
    >
      <DialogTitle id="submit-proposal-title">{title}</DialogTitle>
      <DialogContent>
        {/*
          Who receives it comes first, ahead of any warning, and in the primary text colour with the
          recipient in bold (#5813). It is the one thing a leader has to take in before pressing Submit,
          and below the warning it was the sentence their eye skipped. Prominent without being an Alert
          of its own: it is not a problem, it is what the button does.
        */}
        <DialogContentText id="submit-proposal-recipient" color="text.primary">
          {allowRevert ? "This sends every listed change to " : "This sends the proposal to "}
          {audienceLabel}
          {recipient ? (
            <>
              :{" "}
              <RecipientName
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                {...recipientCard.triggerProps}
              >
                {recipient.fullName}
              </RecipientName>
            </>
          ) : null}
          .
        </DialogContentText>
        {recipientCard.card}
        {/* A line of its own, and "Revert" in the colour of the Revert change buttons it points at. */}
        {allowRevert ? (
          <DialogContentText id="submit-proposal-revert" color="text.primary">
            <Typography component="span" variant="inherit" color="secondary">
              Revert
            </Typography>{" "}
            any change you do not want to submit.
          </DialogContentText>
        ) : null}
        {/* An Alert rather than more prose: this says the submission is going out ahead of someone,
            which is not the same kind of sentence as the one naming who receives it. The same
            component Approve, Approve & Hire and Hire draw, so every confirmation shows one warning. */}
        <ProposalPendingReviewAlert message={message} />
        {message && pendingReviewers ? <Box sx={{ mt: 1.5 }}>{pendingReviewers}</Box> : null}

        {listIsShown && (
          <>
            <Typography id="submit-proposal-changes-heading" variant="subtitle2" component="h3" sx={{ mt: 2 }}>
              {changesHeading}
            </Typography>
            {/*
              Grouped by subject rather than listed in the order the edits happened, and flowed into
              two columns on a wide dialog so a big submission is a page rather than a scroll.
            */}
            <ChangeColumns>
            {sections.map((section) => {
              // The whole fee schedule as one description of itself, drawn exactly as the change
              // history draws it, instead of one row per semantic leaf. It reverts as one thing too:
              // the reader is being shown the schedule, so that is the unit they can restore.
              const { feeScheduleChanges, otherChanges } = partitionProposalFeeScheduleChanges(section.changes);
              const feeScheduleIsReverted =
                feeScheduleChanges.length > 0 && feeScheduleChanges.every(isReverted);
              return (
              <ChangeSection key={section.title}>
                <Typography
                  variant="overline"
                  component="h4"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  {section.title}
                </Typography>
                <List dense disablePadding aria-label={section.title}>
                  {/* No dim on a reverted row: the schedule it draws is the schedule that will be sent,
                      and `opacity` reads as disabled rather than as restored. */}
                  {feeScheduleChanges.length > 0 && (
                    <ListItem key="fee-schedule" sx={changeRowSx}>
                      <ListItemText
                        primary="Fee Schedule"
                        secondary={
                          <ProposalFeeScheduleChangeRedline
                            summary={
                              // Reverted, the row is the restored schedule — every column plain, valued
                              // from the side the revert brings back — rather than the redline of what
                              // this submission would have done to it.
                              feeScheduleIsReverted
                                ? summarizeProposalFeeScheduleRestoredDescriptors(feeScheduleChanges)
                                : summarizeProposalFeeScheduleDescriptors(feeScheduleChanges)
                            }
                          />
                        }
                        slotProps={{ secondary: { component: "span" } }}
                      />
                      {allowRevert ? (
                        <Button
                          size="small"
                          sx={revertButtonSx}
                          color={feeScheduleIsReverted ? "inherit" : "secondary"}
                          startIcon={<RestoreIcon />}
                          onClick={() => toggleRevertAll(feeScheduleChanges, feeScheduleIsReverted)}
                          aria-label={changeToggleName(feeScheduleIsReverted, "Fee Schedule")}
                        >
                          {changeToggleLabel(feeScheduleIsReverted)}
                        </Button>
                      ) : null}
                    </ListItem>
                  )}
                  {otherChanges.map((change) => {
                    const label = changeLabel(change);
                    // Never a blank value: a change with nothing on its new side reads "[Empty]", so an
                    // empty field is distinguishable from a value the dialog could not render.
                    const { previousValue, nextValue } = proposalChangeRedlineValues(change);
                    // ...except when the value would only repeat the name the subject line already gave —
                    // an added document or member, whose value IS its name. Same rule the save
                    // confirmation's list applies, so the two cannot disagree about the duplicate.
                    const showsRedline =
                      previousValue !== undefined || !proposalChangeTransitionCarriesItemLabel(change);
                    const detail = showsRedline ? (
                      <ProposalChangeValueRedline previousValue={previousValue} nextValue={nextValue} />
                    ) : undefined;
                    // A reverted row says what it WILL submit, as a plain value in the secondary tone:
                    // nothing is moving, so there is no transition to redline. It says it even where the
                    // unreverted row suppresses its value as a duplicate of the subject line — an added
                    // document's restored reading ("Not included") is not its name.
                    const restoredValue = proposalChangeRestoredValueText(change);
                    return (
                      // No dim: the row now carries the value being sent, and `opacity` reads as disabled.
                      <ListItem key={change.key} sx={changeRowSx}>
                        <ListItemText
                          primary={label}
                          secondary={isReverted(change) ? restoredValue : detail}
                          slotProps={{ secondary: { component: "span" } }}
                        />
                        {allowRevert ? (
                          <Button
                            size="small"
                            sx={revertButtonSx}
                            color={isReverted(change) ? "inherit" : "secondary"}
                            startIcon={<RestoreIcon />}
                            onClick={() => toggleRevert(change)}
                            aria-label={changeToggleName(isReverted(change), label)}
                          >
                            {changeToggleLabel(isReverted(change))}
                          </Button>
                        ) : null}
                      </ListItem>
                    );
                  })}
                </List>
              </ChangeSection>
              );
            })}
            </ChangeColumns>
          </>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Fee Schedule — Essential fixed fee" reads better in a list than the full one-line sentence. */
function changeLabel(change: ProposalChangeDescriptor): string {
  return change.itemLabel ? `${change.fieldLabel} — ${change.itemLabel}` : change.fieldLabel;
}

/**
 * The two states of one toggle, written as a matching pair.
 *
 * It read "Revert" against "Keep change" before, which is not a pair: one is a bare verb and the other a
 * verb with a noun, and "Keep" does not name the act that undoes "Revert" (#5563). "Revert change" and
 * "Restore change" are the same shape, and each says what the click does to the row it sits on.
 */
function changeToggleLabel(isReverted: boolean): string {
  return isReverted ? "Restore change" : "Revert change";
}

/**
 * The accessible name for that toggle.
 *
 * Every row shows the same two words, so the name has to add the field the row is about — a screen reader
 * moving button to button hears "Revert change" twelve times otherwise. It starts with the visible label
 * exactly, because an `aria-label` replaces the visible text for a name, and a name that does not contain
 * the label it replaces breaks speech control (WCAG 2.5.3 Label in Name).
 */
function changeToggleName(isReverted: boolean, label: string): string {
  return `${changeToggleLabel(isReverted)}: ${label}`;
}
